import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUp, ChevronDown, Trash2, Star, ImagePlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FileUpload } from '@/components/ui/file-upload';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface ProductImage {
  id: string;
  url: string;
  alt: string | null;
  isPrimary: boolean;
  sortOrder: number;
}

interface ProductImageManagerProps {
  productId: string;
  images: ProductImage[];
}

export function ProductImageManager({ productId, images }: ProductImageManagerProps) {
  const { t } = useTranslation('trading');
  const utils = trpc.useUtils();

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const invalidate = () => {
    utils.tradingProduct.byId.invalidate(productId);
  };

  const addImage = trpc.tradingProduct.addImage.useMutation({
    onSuccess: () => {
      invalidate();
      showSuccessToast(t('products.imageAdded', 'Görsel eklendi'));
    },
    onError: (err) => showErrorToast(err.message),
  });

  const updateImage = trpc.tradingProduct.updateImage.useMutation({
    onSuccess: () => {
      invalidate();
    },
    onError: (err) => showErrorToast(err.message),
  });

  const deleteImage = trpc.tradingProduct.deleteImage.useMutation({
    onSuccess: () => {
      setDeleteConfirmId(null);
      invalidate();
      showSuccessToast(t('products.imageDeleted', 'Görsel silindi'));
    },
    onError: (err) => showErrorToast(err.message),
  });

  const setPrimaryImage = trpc.tradingProduct.setPrimaryImage.useMutation({
    onSuccess: () => {
      invalidate();
      showSuccessToast(t('products.primaryImageSet', 'Ana görsel güncellendi'));
    },
    onError: (err) => showErrorToast(err.message),
  });

  const handleAltBlur = (img: ProductImage, value: string) => {
    if (value === (img.alt ?? '')) return;
    updateImage.mutate({ id: img.id, data: { alt: value } });
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const current = images[index];
    const previous = images[index - 1];
    if (!current || !previous) return;
    updateImage.mutate({ id: current.id, data: { sortOrder: previous.sortOrder } });
    updateImage.mutate({ id: previous.id, data: { sortOrder: current.sortOrder } });
  };

  const handleMoveDown = (index: number) => {
    if (index === images.length - 1) return;
    const current = images[index];
    const next = images[index + 1];
    if (!current || !next) return;
    updateImage.mutate({ id: current.id, data: { sortOrder: next.sortOrder } });
    updateImage.mutate({ id: next.id, data: { sortOrder: current.sortOrder } });
  };

  const handleDeleteRequest = (imgId: string) => {
    setDeleteConfirmId(imgId);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmId) {
      deleteImage.mutate(deleteConfirmId);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  return (
    <div className="space-y-4">
      {/* Upload zone */}
      <FileUpload
        public
        accept="image/*"
        folder="trading-products"
        onUploaded={({ fileUrl }) =>
          addImage.mutate({
            productId,
            url: fileUrl,
            alt: '',
            isPrimary: images.length === 0,
            sortOrder: images.length,
          })
        }
      />

      {/* Delete confirmation inline banner */}
      {deleteConfirmId && (
        <div className="flex items-center justify-between gap-2 border border-destructive/50 bg-destructive/5 px-3 py-2 text-xs">
          <span className="text-destructive">
            {t('products.confirmDeleteImage', 'Bu görseli silmek istediğinizden emin misiniz?')}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={handleDeleteCancel}
            >
              {t('common.cancel', 'Vazgeç')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={handleDeleteConfirm}
              disabled={deleteImage.isPending}
            >
              {t('common.delete', 'Sil')}
            </Button>
          </div>
        </div>
      )}

      {/* Image grid */}
      {images.length > 0 ? (
        <div className="grid grid-cols-2 gap-2">
          {images.map((img, index) => (
            <div
              key={img.id}
              className={`relative border ${
                img.isPrimary ? 'border-primary' : 'border-border'
              } space-y-1.5 p-1.5`}
            >
              {/* Thumbnail */}
              <div className="relative aspect-square">
                <img
                  src={img.url}
                  alt={img.alt ?? ''}
                  className="h-full w-full object-cover"
                  referrerPolicy="no-referrer"
                  loading="lazy"
                />
                {img.isPrimary && (
                  <span className="absolute top-1 left-1 bg-primary px-1 py-0.5 text-[10px] text-primary-foreground flex items-center gap-0.5">
                    <Star className="h-2.5 w-2.5" />
                    {t('products.primaryBadge', 'Ana')}
                  </span>
                )}
              </div>

              {/* Alt text input */}
              <input
                type="text"
                defaultValue={img.alt ?? ''}
                placeholder={t('products.altTextPlaceholder', 'Alt metin')}
                className="w-full border border-border bg-background px-1.5 py-0.5 text-[11px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                onBlur={(e) => handleAltBlur(img, e.target.value)}
              />

              {/* Controls */}
              <div className="flex items-center gap-1 flex-wrap">
                {!img.isPrimary && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 px-1.5 text-[10px] flex-1"
                    onClick={() => setPrimaryImage.mutate(img.id)}
                    disabled={setPrimaryImage.isPending}
                  >
                    {t('products.setAsPrimary', 'Ana Görsel Yap')}
                  </Button>
                )}

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0 || updateImage.isPending}
                  title={t('products.moveUp', 'Yukarı taşı')}
                >
                  <ChevronUp className="h-3 w-3" />
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => handleMoveDown(index)}
                  disabled={index === images.length - 1 || updateImage.isPending}
                  title={t('products.moveDown', 'Aşağı taşı')}
                >
                  <ChevronDown className="h-3 w-3" />
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => handleDeleteRequest(img.id)}
                  title={t('common.delete', 'Sil')}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-6 text-center border border-dashed border-border">
          <ImagePlus className="h-8 w-8 text-muted-foreground/50" />
          <p className="mt-2 text-xs text-muted-foreground">
            {t('products.noImages', 'Henüz görsel eklenmemiş')}
          </p>
        </div>
      )}
    </div>
  );
}
